import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, LogOut, User, Menu, RefreshCw, WifiOff, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { authApi } from '../../api/auth';
import { toast } from '../ui/Toast';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

interface HeaderProps {
  onMenuToggle?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { mode, isDark, toggle: toggleTheme } = useThemeStore();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false);
  const [mobileSearchValue, setMobileSearchValue] = React.useState('');
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const isOnline = useOnlineStatus();

  // Auto-focus when mobile search overlay opens
  useEffect(() => {
    if (mobileSearchOpen) {
      setTimeout(() => mobileSearchInputRef.current?.focus(), 50);
    } else {
      setMobileSearchValue('');
    }
  }, [mobileSearchOpen]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileSearchOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Only refetch active queries (visible on screen), don't invalidate all caches
    await queryClient.refetchQueries({ type: 'active' });
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Continue with local logout even if API fails
    }
    logout();
    toast.info('Abgemeldet', 'Du wurdest erfolgreich abgemeldet.');
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-30 relative bg-white/80 dark:bg-[#1a1a2e]/80 backdrop-blur-md border-b border-neutral-200 dark:border-indigo-500/10"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* Offline-Banner */}
      {!isOnline && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-400 dark:bg-yellow-500 text-yellow-900 text-sm font-medium"
        >
          <WifiOff className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span>
            Du bist offline — Aenderungen werden synchronisiert, sobald du wieder online bist.
          </span>
        </div>
      )}
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        {/* Left: Menu button (mobile) + Logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img src="/logo.png" alt="PaarKurs" className="w-8 h-8 rounded-lg object-contain" />
            <span className="hidden sm:block text-lg font-bold text-neutral-800 dark:text-white">
              PaarKurs
            </span>
          </div>
        </div>

        {/* Center: Search (desktop only) */}
        <div className="hidden md:flex flex-1 max-w-md mx-8">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Belege durchsuchen..."
              className="w-full pl-10 pr-4 py-2 text-sm bg-neutral-100 dark:bg-[#1e1e2e]/60 border border-transparent dark:border-indigo-500/20 rounded-lg
                         placeholder:text-neutral-400 dark:placeholder:text-neutral-500 dark:text-white
                         focus:bg-white dark:focus:bg-[#1e1e2e] focus:border-neutral-300 dark:focus:border-indigo-500/40
                         focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const value = (e.target as HTMLInputElement).value;
                  if (value.trim()) {
                    navigate(`/receipts?search=${encodeURIComponent(value.trim())}`);
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Mobile Search Button — only visible on small screens */}
          <button
            onClick={() => setMobileSearchOpen(true)}
            className="md:hidden p-2 rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors"
            title="Suche"
            aria-label="Suche öffnen"
          >
            <Search className="w-5 h-5" />
          </button>

          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors"
            title="Daten aktualisieren"
          >
            <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 transition-all duration-300"
            title={mode === 'system' ? 'System (Auto)' : isDark ? 'Light Mode' : 'Dark Mode'}
          >
            <span className="text-lg">{mode === 'system' ? '\uD83D\uDCBB' : isDark ? '\u2600\uFE0F' : '\uD83C\uDF19'}</span>
          </button>

          {/* User Menu */}
          <div className="relative group">
            <button className="flex items-center gap-2 p-1.5 pl-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors">
              <span className="hidden sm:block text-sm font-medium text-neutral-700 dark:text-neutral-200">
                {user?.name || 'Benutzer'}
              </span>
              <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
            </button>

            {/* Dropdown */}
            <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-[#1e1e2e] rounded-xl border border-neutral-200 dark:border-indigo-500/15 shadow-lg
                            opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="p-2">
                <button
                  onClick={() => navigate('/settings')}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 rounded-lg hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors"
                >
                  <User className="w-4 h-4" />
                  Einstellungen
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-error-500 rounded-lg hover:bg-error-50 dark:hover:bg-error-500/10 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Abmelden
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Search Overlay */}
      {mobileSearchOpen && (
        <div className="md:hidden absolute inset-x-0 top-0 z-50 flex items-center gap-2 h-16 px-4 bg-white/95 dark:bg-[#1a1a2e]/95 backdrop-blur-md border-b border-neutral-200 dark:border-indigo-500/10"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
            <input
              ref={mobileSearchInputRef}
              type="text"
              value={mobileSearchValue}
              onChange={(e) => setMobileSearchValue(e.target.value)}
              placeholder="Belege durchsuchen..."
              className="w-full pl-10 pr-4 py-2 text-sm bg-neutral-100 dark:bg-[#1e1e2e]/60 border border-transparent dark:border-indigo-500/20 rounded-lg
                         placeholder:text-neutral-400 dark:placeholder:text-neutral-500 dark:text-white
                         focus:bg-white dark:focus:bg-[#1e1e2e] focus:border-neutral-300 dark:focus:border-indigo-500/40
                         focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const value = mobileSearchValue.trim();
                  if (value) {
                    navigate(`/receipts?search=${encodeURIComponent(value)}`);
                  }
                  setMobileSearchOpen(false);
                }
              }}
            />
          </div>
          <button
            onClick={() => setMobileSearchOpen(false)}
            className="p-2 rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors shrink-0"
            aria-label="Suche schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </header>
  );
};
